import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc, limit, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { canAddDevice } from '../utils/permissions';
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaNetworkWired,
  FaMapMarkerAlt,
  FaBatteryFull,
  FaTachometerAlt,
  FaClock,
  FaSignal,
  FaCheckCircle
} from 'react-icons/fa';

const Devices = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterIgnition, setFilterIgnition] = useState('all');
  const [filterApi, setFilterApi] = useState('all');
  const [, setError] = useState('');
  const [, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [canAdd, setCanAdd] = useState(false);

  useEffect(() => {
    fetchUserRole();
    fetchDevices();
  }, []);

  const fetchUserRole = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        // First, try to get user document by document ID (matching auth UID)
        let userDoc = await getDoc(doc(db, 'users', user.uid));
        
        // If document doesn't exist with that ID, try querying by uid field
        if (!userDoc.exists()) {
          const usersRef = collection(db, 'users');
          const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
          const querySnapshot = await getDocs(uidQuery);
          
          if (!querySnapshot.empty) {
            // Found user document with uid field matching auth UID
            userDoc = querySnapshot.docs[0];
          }
        }
        
        if (userDoc.exists()) {
          const role = userDoc.data().role;
          setCanAdd(canAddDevice(role));
        }
      } catch (err) {
        console.error('Error fetching user role:', err);
      }
    }
  };

  const fetchDevices = async () => {
    setLoading(true);
    setError('');
    try {
      const devicesRef = collection(db, 'devices');
      const q = query(devicesRef, orderBy('lastUpdated', 'desc'));
      const snapshot = await getDocs(q);
      
      // Map devices and deduplicate by ID (in case of any duplicates)
      const devicesMap = new Map();
      snapshot.docs.forEach(doc => {
        const deviceData = {
          id: doc.id,
          ...doc.data()
        };
        // Use device ID as key to ensure uniqueness
        devicesMap.set(doc.id, deviceData);
      });
      
      // Convert map to array and deduplicate by IMEI as well
      const devicesArray = Array.from(devicesMap.values());
      const uniqueDevices = [];
      const seenImeis = new Set();
      
      devicesArray.forEach(device => {
        const imei = device.imeiNumber ? String(device.imeiNumber).trim() : null;
        const deviceName = device.deviceName ? String(device.deviceName).trim() : null;
        const apiProvider = device.apiProvider || '';
        
        // Create a unique key: IMEI if available, otherwise deviceName + apiProvider
        const uniqueKey = imei 
          ? `imei:${imei}` 
          : (deviceName ? `name:${deviceName}:${apiProvider}` : `id:${device.id}`);
        
        // Only add if we haven't seen this unique key before
        if (!seenImeis.has(uniqueKey)) {
          seenImeis.add(uniqueKey);
          uniqueDevices.push(device);
        }
      });
      
      // Sort by lastUpdated descending
      uniqueDevices.sort((a, b) => {
        const dateA = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
        const dateB = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
        return dateB - dateA;
      });
      
      setDevices(uniqueDevices);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to fetch devices');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        await deleteDoc(doc(db, 'devices', deviceId));
        setSuccess('Device deleted successfully');
        fetchDevices();
      } catch (error) {
        console.error('Error deleting device:', error);
        setError('Failed to delete device');
      }
    }
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch = 
      (device.deviceName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (device.imeiNumber || '').toString().includes(searchTerm) ||
      (device.location || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || device.status === filterStatus;
    
    const matchesIgnition = filterIgnition === 'all' || 
      (filterIgnition === 'on' && device.isIgnitionOn) ||
      (filterIgnition === 'off' && !device.isIgnitionOn);
    
    const matchesApi = filterApi === 'all' || 
      (device.apiProvider && device.apiProvider.toLowerCase() === filterApi.toLowerCase());
    
    return matchesSearch && matchesStatus && matchesIgnition && matchesApi;
  });

  // Pagination logic
  const itemsPerPage = 9;
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);
  const paginatedDevices = filteredDevices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterIgnition, filterApi]);

  // Get unique API providers from devices
  const apiProviders = [...new Set(devices.map(d => d.apiProvider).filter(Boolean))];

  // Algorithm to assess device health and signal
  const assessDeviceHealth = (device) => {
    let score = 100; // Start with perfect score, deduct points for issues
    
    // Parse attributes if it's a string
    let attributes = {};
    try {
      if (typeof device.attributes === 'string') {
        attributes = JSON.parse(device.attributes || '{}');
      } else if (device.attributes) {
        attributes = device.attributes;
      }
    } catch (e) {
      // If parsing fails, use empty object
      attributes = {};
    }

    // Check last update time (stale data = bad)
    if (device.lastUpdated) {
      const lastUpdate = new Date(device.lastUpdated);
      const now = new Date();
      const minutesSinceUpdate = (now - lastUpdate) / (1000 * 60);
      
      if (minutesSinceUpdate > 60) {
        score -= 40; // Very stale data
      } else if (minutesSinceUpdate > 30) {
        score -= 25; // Stale data
      } else if (minutesSinceUpdate > 15) {
        score -= 10; // Somewhat stale
      }
    } else {
      score -= 30; // No update time available
    }

    // Check battery level
    const battery = device.battery || 0;
    if (battery < 10) {
      score -= 30; // Critical battery
    } else if (battery < 20) {
      score -= 15; // Low battery
    } else if (battery < 50) {
      score -= 5; // Medium battery
    }

    // Check signal strength from attributes
    const signalStrength = attributes.SignalStrength || attributes.signalStrength || attributes.signal || null;
    if (signalStrength !== null) {
      const signal = typeof signalStrength === 'string' ? parseFloat(signalStrength) : signalStrength;
      if (signal < -100) {
        score -= 30; // Very weak signal
      } else if (signal < -90) {
        score -= 20; // Weak signal
      } else if (signal < -80) {
        score -= 10; // Moderate signal
      }
    }

    // Check GPS accuracy/quality from attributes
    const gpsQuality = attributes.GPSQuality || attributes.gpsQuality || attributes.gps || null;
    if (gpsQuality !== null) {
      const quality = typeof gpsQuality === 'string' ? parseFloat(gpsQuality) : gpsQuality;
      if (quality < 3) {
        score -= 20; // Poor GPS quality
      } else if (quality < 5) {
        score -= 10; // Moderate GPS quality
      }
    }

    // Check for error flags in attributes
    const hasError = attributes.Error || attributes.error || attributes.HasError || false;
    if (hasError) {
      score -= 25; // Device has errors
    }

    // Check if coordinates are valid (not 0,0 or null)
    const lat = parseFloat(device.lat) || 0;
    const lon = parseFloat(device.lon) || 0;
    if (lat === 0 && lon === 0) {
      score -= 15; // Invalid coordinates
    }

    // Determine health status
    if (score >= 75) {
      return { status: 'good', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
    } else if (score >= 50) {
      return { status: 'weak', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
    } else {
      return { status: 'poor', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Devices Management</h1>
        <div className="flex items-center space-x-4">
          {canAdd && (
            <button
              onClick={() => navigate('/devices/create')}
              className="bg-[#00E0E0] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#00B8B8] transition-colors"
            >
              <FaPlus /> Add Device
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700">Loading Devices...</h2>
            <p className="text-gray-500 mt-2">Please wait while we fetch your device data</p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
        </div>
        <div className="w-48">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">In Maintenance</option>
          </select>
        </div>
        <div className="w-48">
          <select
            value={filterIgnition}
            onChange={(e) => setFilterIgnition(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Ignition</option>
            <option value="on">ON</option>
            <option value="off">OFF</option>
          </select>
        </div>
        <div className="w-48">
          <select
            value={filterApi}
            onChange={(e) => setFilterApi(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All APIs</option>
            {apiProviders.map(api => (
              <option key={api} value={api.toLowerCase()}>
                {api.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDevices.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FaNetworkWired className="mx-auto text-gray-400 text-6xl mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No devices found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first device'}
            </p>
            {!searchTerm && filterStatus === 'all' && canAdd && (
              <button
                onClick={() => navigate('/devices/create')}
                className="bg-[#00E0E0] text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-[#00B8B8] transition-colors mx-auto"
              >
                <FaPlus /> Add Device
              </button>
            )}
          </div>
        ) : (
          paginatedDevices.map((device) => {
            const health = assessDeviceHealth(device);
            return (
            <div key={device.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 flex items-stretch overflow-hidden relative">
              {/* Right: Action Icons */}
              <div className="flex flex-col items-end w-1/3 min-w-[120px] max-w-[180px] absolute top-0 right-0 h-full p-4 pt-3 z-10">
                {/* Action Icons */}
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => handleDelete(device.id)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
                {/* Device Icon */}
                <div className="w-full flex flex-col items-end mt-auto relative">
                  <div className="w-20 h-20 rounded-lg bg-[#00E0E0]/10 flex items-center justify-center">
                    <FaNetworkWired className="text-[#00E0E0] text-3xl" />
                  </div>
                  {/* Green check icon if device is linked to a vehicle */}
                  {device.vehicleId && (
                    <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1.5 shadow-lg">
                      <FaCheckCircle className="text-white text-sm" />
                    </div>
                  )}
                </div>
              </div>

              {/* Left: Info */}
              <div className="flex-1 flex flex-col justify-between pr-[calc(33%+1.5rem)]">
                <div>
                  <div className="flex flex-col gap-1 mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <FaNetworkWired className="text-[#00E0E0]" />
                      {device.deviceName || 'Unknown Device'}
                    </h3>
                    {device.eventName && (
                      <p className="text-sm font-medium text-[#00E0E0]">{device.eventName}</p>
                    )}
                    {device.location && (
                      <p className="text-sm font-bold text-gray-600">{device.location}</p>
                    )}
                    <p className="text-gray-600 font-medium">IMEI: {device.imeiNumber || 'N/A'}</p>
                  </div>

                  {/* Device Status Info */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-100 w-full">
                    <h4 className="font-semibold text-gray-700 mb-3">Current Status</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Speed:</span>
                        <span className="font-medium text-gray-800">{device.vehicleSpeed || 0} km/h</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Ignition:</span>
                        <span className={`font-medium ${device.isIgnitionOn ? 'text-green-600' : 'text-gray-600'}`}>
                          {device.isIgnitionOn ? 'ON' : 'OFF'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Altitude:</span>
                        <span className="font-medium text-gray-800">{device.altitude || '0'} m</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="flex items-center gap-2">
                      <FaTachometerAlt className="text-gray-400" />
                      Odometer: {device.odometer || 0} km
                    </p>
                    <p className="flex items-center gap-2">
                      <FaBatteryFull className="text-gray-400" />
                      Battery: {device.battery || 0}%
                    </p>
                    <p className="flex items-center gap-2">
                      <FaMapMarkerAlt className="text-gray-400" />
                      Coordinates: {device.lat}, {device.lon}
                    </p>
                    <p className="flex items-center gap-2">
                      <FaClock className="text-gray-400" />
                      Last Update: {device.lastUpdated ? new Date(device.lastUpdated).toLocaleString() : 'N/A'}
                    </p>
                    {device.workingHours !== undefined && (
                      <p className="flex items-center gap-2">
                        <FaClock className="text-gray-400" />
                        Working Hours: {device.workingHours || 0}
                      </p>
                    )}
                    {device.apiProvider && (
                      <p className="flex items-center gap-2">
                        <FaSignal className="text-gray-400" />
                        API: {device.apiProvider.toUpperCase()}
                      </p>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      device.status === 'active' ? 'bg-green-100 text-green-800' :
                      device.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {device.status ? device.status.charAt(0).toUpperCase() + device.status.slice(1) : 'Active'}
                    </span>
                    {/* Health Status Badge - Next to Status */}
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${health.bgColor} ${health.textColor}`}>
                      {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {filteredDevices.length > itemsPerPage && (
        <div className="flex justify-end items-center mt-4 space-x-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-semibold bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default Devices;

